import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/src/supabase/client';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'Discussion' | 'Comment';
  itemId: string;
  userId: string;
}

const reportReasons = [
  "Spam or promotional content",
  "Inappropriate or offensive content",
  "Harassment or hate speech",
  "Misinformation or factually incorrect",
  "Violates community guidelines",
  "Duplicate content"
];

const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  type,
  itemId,
  userId
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [additionalComments, setAdditionalComments] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  
  const reportMutation = useMutation({
    mutationFn: async () => {
      if (!selectedReason) {
        throw new Error("Please select a reason for reporting");
      }
      
      const { error } = await supabase
        .from('forums_reports')
        .insert({
          user_id: userId,
          ...(type === 'Discussion' ? { discussion_id: itemId } : { comment_id: itemId }),
          reason: selectedReason,
          additional_comments: additionalComments || null
        });
        
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSelectedReason('');
        setAdditionalComments('');
        setSuccess(false);
      }, 1500);
    },
    onError: (error: any) => {
      setError(error.message || "Failed to submit report. Please try again.");
    }
  });
  
  if (!isOpen) return null;
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedReason) {
      setError("Please select a reason for reporting");
      return;
    }
    
    setError(null);
    reportMutation.mutate();
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">
            Report {type}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {success ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">Report Submitted</h4>
            <p className="text-gray-600">Thank you for helping keep our community safe.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for reporting *
              </label>
              <div className="space-y-2">
                {reportReasons.map(reason => (
                  <label key={reason} className="flex items-start">
                    <input
                      type="radio"
                      name="reportReason"
                      value={reason}
                      checked={selectedReason === reason}
                      onChange={() => setSelectedReason(reason)}
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-gray-700">{reason}</span>
                  </label>
                ))}
                <label className="flex items-start">
                  <input
                    type="radio"
                    name="reportReason"
                    value="Other"
                    checked={selectedReason === "Other"}
                    onChange={() => setSelectedReason("Other")}
                    className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-gray-700">Other</span>
                </label>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional comments (optional)
              </label>
              <textarea
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                rows={3}
                placeholder="Please provide any additional details..."
              ></textarea>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-800 rounded-md">
                {error}
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md mr-2 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={reportMutation.isPending}
                className={`px-4 py-2 ${
                  reportMutation.isPending
                    ? 'bg-indigo-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                } text-white rounded-md transition-colors`}
              >
                {reportMutation.isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  'Submit Report'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportModal;
